import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  X,
  Plus
} from 'lucide-react';

const ProductSelection = ({ 
  campaign, 
  selectedProducts = [], 
  allProducts = [], 
  onUpdate,
  api,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const [productSearch, setProductSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [availableBrands, setAvailableBrands] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    brand: '',
    sku: '',
    barcode: '',
    description: '',
    is_active: true
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);
  const [lastSavedProducts, setLastSavedProducts] = useState(selectedProducts);

  // Extract unique brands from products
  useEffect(() => {
    const brands = [...new Set(allProducts.map(p => p.brand).filter(Boolean))];
    setAvailableBrands(brands.sort());
  }, [allProducts]);

  // Reset last saved products when switching campaigns
  useEffect(() => {
    setLastSavedProducts(selectedProducts);
  }, [campaign?.id]);

  const notifySuccess = (message) => {
    if (setSuccessMessage && setShowSuccess) {
      setSuccessMessage(message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } else {
      alert(message);
    }
  };

  const notifyError = (message) => {
    if (setErrorMessage && setShowError) {
      setErrorMessage(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } else {
      alert(message);
    }
  };

  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  };

  const hasChanges = useMemo(() => {
    return !arraysEqual(selectedProducts, lastSavedProducts);
  }, [selectedProducts, lastSavedProducts]);

  const filteredProducts = allProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                         (product.sku && product.sku.toLowerCase().includes(productSearch.toLowerCase()));
    const matchesBrand = selectedBrand === 'all' || product.brand === selectedBrand;
    return matchesSearch && matchesBrand;
  });

  const toggleProductSelection = (productId) => {
    const newSelected = selectedProducts.includes(productId)
      ? selectedProducts.filter(id => id !== productId)
      : [...selectedProducts, productId];
    
    onUpdate(newSelected);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredProducts.map(p => p.id);
    const allSelected = allFilteredIds.every(id => selectedProducts.includes(id));
    
    if (allSelected) {
      // Deselect all filtered products
      const newSelected = selectedProducts.filter(id => !allFilteredIds.includes(id));
      onUpdate(newSelected);
    } else {
      // Select all filtered products
      const newSelected = [...new Set([...selectedProducts, ...allFilteredIds])];
      onUpdate(newSelected);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setSavingProduct(true);

    try {
      const response = await api.post('/admin/products', newProduct);
      const createdProduct = response.data;
      
      // Add to allProducts (parent should refetch)
      onUpdate([...selectedProducts, createdProduct.id], true); // true indicates refresh needed
      
      // Reset form and close modal
      setNewProduct({
        name: '',
        brand: '',
        sku: '',
        barcode: '',
        description: '',
        is_active: true
      });
      setShowAddProduct(false);
      notifySuccess('Product created successfully!');
    } catch (err) {
      console.error('Create product error:', err);
      notifyError('Failed to create product: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingProduct(false);
    }
  };

  const handleSaveSelection = async () => {
    if (!campaign?.id) {
      notifyError('Campaign information is missing. Please reload the page and try again.');
      return;
    }

    setSavingSelection(true);

    try {
      await api.put(`/admin/campaigns/${campaign.id}/products`, {
        product_ids: selectedProducts
      });

      setLastSavedProducts(selectedProducts);
      notifySuccess('Product selection saved successfully!');
    } catch (err) {
      console.error('Save product selection error:', err);
      const message = err.response?.data?.error || 'Failed to save product selection.';
      notifyError(message);
    } finally {
      setSavingSelection(false);
    }
  };

  const allFilteredSelected = filteredProducts.length > 0 && 
    filteredProducts.every(p => selectedProducts.includes(p.id));

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Product Selection</h3>
          {hasChanges && (
            <p className="mt-1 text-sm text-amber-600">You have unsaved changes.</p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleSaveSelection}
            disabled={!hasChanges || savingSelection}
            className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSelection ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => setShowAddProduct(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600"
          >
            <Plus className="w-5 h-5" />
            <span>Add New Product</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex">
          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
            <Search className="w-5 h-5" />
          </span>
          <input
            type="text"
            placeholder="Search products by name or SKU..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </div>
        
        <select
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
          value={selectedBrand}
          onChange={(e) => setSelectedBrand(e.target.value)}
        >
          <option value="all">All Brands</option>
          {availableBrands.map(brand => (
            <option key={brand} value={brand}>{brand}</option>
          ))}
        </select>
      </div>

      {/* Selected Products Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-800">
          <span className="font-semibold">{selectedProducts.length}</span> products selected for this campaign
        </p>
      </div>

      {/* Product List with Checkboxes */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Select All Header */}
        {filteredProducts.length > 0 && (
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleSelectAll}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">
                Select All ({filteredProducts.length} products)
              </span>
            </label>
          </div>
        )}

        {/* Scrollable Product List */}
        <div className="max-h-96 overflow-y-auto">
          {filteredProducts.map(product => (
            <label 
              key={product.id}
              className="flex items-start px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
            >
              <input
                type="checkbox"
                checked={selectedProducts.includes(product.id)}
                onChange={() => toggleProductSelection(product.id)}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-0.5"
              />
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                <div className="text-sm text-gray-600">
                  {product.brand}
                  {product.sku && <span className="ml-2 text-xs">• SKU: {product.sku}</span>}
                </div>
              </div>
            </label>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No products found matching your search criteria.</p>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add New Product</h3>
              <button
                type="button"
                onClick={() => setShowAddProduct(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name*
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand*
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newProduct.brand}
                  onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newProduct.sku}
                  onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Barcode
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newProduct.barcode}
                  onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows="3"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                  checked={newProduct.is_active}
                  onChange={(e) => setNewProduct({ ...newProduct, is_active: e.target.checked })}
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Product is active
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddProduct(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProduct}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
                >
                  {savingProduct ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelection;
